import { tWithKeyAttr as t } from "../i18nHelper";
import * as Yup from "yup";
import { phoneRegExp } from "./constants";
import { isAllowedExternalVideoUrl } from "./utils";

export const supportApplicationValidationSchema = Yup.object().shape({
  subject: Yup.string().required("VALIDATION_REQUIRED_FIELD"),
  body: Yup.string().required("VALIDATION_REQUIRED_FIELD"),
});

export const signUpValidationSchema1 = Yup.object().shape({
  firstName: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(2, "VALIDATION_MIN_FIRST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  lastName: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(2, "VALIDATION_MIN_LAST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  passport: Yup.string()
    .required(t("VALIDATION_REQUIRED_FIELDS"))
    .min(9, "VALIDATION_PASSPORT_LENGTH")
    .max(9, "VALIDATION_PASSPORT_LENGTH"),
});

export const signUpValidationSchema2 = Yup.object().shape({
  email: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .email("VALIDATION_INVALID_EMAIL"),
  phone: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .matches(phoneRegExp, "VALIDATION_INVALID_PHONE"),
  birth: Yup.date()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(new Date(1920, 0, 1), "VALIDATION_INVALID_BIRTHDATE")
    .max(new Date(), "VALIDATION_INVALID_BIRTHDATE"),
});

export const loginEmailValidationSchema = Yup.object().shape({
  email: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .email("VALIDATION_INVALID_EMAIL"),
  password: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(6, "VALIDATION_MIN_PASSWORD"),
  roleId: Yup.string().required("VALIDATION_REQUIRED_FIELDS"),
});

export const loginSMSValidationSchema = Yup.object().shape({
  phone: Yup.string()
    .required("VALIDATION_REQUIRED_PHONE")
    .matches(phoneRegExp, "VALIDATION_INVALID_PHONE"),
});

export const changeAvatarValidationSchema = Yup.object()
  .shape({
    avatar: Yup.string(),
    link: Yup.string().url("VALIDATION_INVALID_URL"),
  })
  .test("at-least-one", "VALIDATION_AT_LEAST_ONE_AVATAR_OR_LINK", (values) => {
    return !!values.link || !!values.avatar; // At least one must be filled
  });
export const changeVideoValidationSchema = Yup.object()
  .shape({
    video: Yup.mixed().nullable(),
    link: Yup.string()
      .trim()
      .url("VALIDATION_INVALID_URL")
      .test("allowed-video-host", "ONLY_YOUTUBE_OR_VIMEO", (value) => {
        return !value || isAllowedExternalVideoUrl(value);
      }),
  })
  .test("at-least-one", "VALIDATION_AT_LEAST_ONE_VIDEO_OR_LINK", (values) => {
    return !!values?.link || !!values?.video;
  });
export const restorePasswordValidationSchema = Yup.object().shape({
  email_or_phone: Yup.string()
    .required("VALIDATION_REQUIRED_EMAIL_OR_PHONE")
    .test("email_or_phone", "VALIDATION_INVALID_EMAIL_OR_PHONE", (value) => {
      if (value && value.includes("@")) {
        return validateEmail(value);
      } else return phoneRegExp.test(value || "");
    }),
});

const validateEmail = (email: string) => {
  return Yup.string().email().isValidSync(email);
};

export const updateUserValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(2, "VALIDATION_MIN_FIRST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  lastName: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(2, "VALIDATION_MIN_LAST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  email: Yup.string()
    .required(t("VALIDATION_REQUIRED_FIELDS"))
    .email(t("VALIDATION_INVALID_EMAIL")),
  phone: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .matches(phoneRegExp, "VALIDATION_INVALID_PHONE"),
  degree: Yup.string(),
  about: Yup.string(),
});

export const addNewUserValidationSchema = Yup.object().shape({
  firstName: Yup.string()
    .required("VALIDATION_REQUIRED_FIELDS")
    .min(2, "VALIDATION_MIN_FIRST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  lastName: Yup.string().min(2, "VALIDATION_MIN_LAST_NAME"),
  // .matches(/^[A-Za-zא-ת0-9\s\-]+$/,"VALIDATION_ALPHA_NUMERIC_ONLY")),
  // A patient must have at least one contact method: phone or email.
  // Each is required only while the other is empty, so both flag red until one is filled.
  phone: Yup.string()
    .matches(phoneRegExp, { message: "VALIDATION_INVALID_PHONE", excludeEmptyString: true })
    .when("email", {
      is: (email: string) => !email,
      then: (schema) => schema.required("VALIDATION_REQUIRED_FIELDS"),
    }),
  email: Yup.string()
    .email("VALIDATION_INVALID_EMAIL")
    .when("phone", {
      is: (phone: string) => !phone,
      then: (schema) => schema.required("VALIDATION_REQUIRED_FIELDS"),
    }),
  birth: Yup.date()
    .min(new Date(1920, 0, 1), "VALIDATION_INVALID_BIRTHDATE")
    .max(new Date(), "VALIDATION_INVALID_BIRTHDATE"),
  passport: Yup.string()
    .min(9, "VALIDATION_PASSPORT_LENGTH")
    .max(9, "VALIDATION_PASSPORT_LENGTH"),
  // email <-> phone reference each other; declare the pair so Yup skips cyclic ordering
}, [["email", "phone"]]);

export const passwordValidationSchema = Yup.object().shape({
  password: Yup.string()
    .required(t("VALIDATION_REQUIRED_PASSWORD"))
    .min(6, "VALIDATION_MIN_PASSWORD")
    .test("password-requirements", function (value) {
      if (!value)
        return this.createError({ message: "VALIDATION_REQUIRED_PASSWORD" });

      const hasLetter = /[A-Za-zא-ת]/.test(value);
      const hasDigit = /\d/.test(value);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

      // If the password contains all three types, it's valid
      if (hasLetter && hasDigit && hasSpecialChar) return true;

      // Generate the correct error message based on what is missing
      if (!hasLetter && !hasSpecialChar) {
        return this.createError({
          message: "VALIDATION_PASSWORD_LETTER_SPECIAL",
        });
      }
      if (!hasLetter && !hasDigit) {
        return this.createError({
          message: "VALIDATION_PASSWORD_LETTER_NUMBER",
        });
      }
      if (!hasDigit && !hasSpecialChar) {
        return this.createError({
          message: "VALIDATION_PASSWORD_NUMBER_SPECIAL",
        });
      }
      if (!hasDigit) {
        return this.createError({ message: "VALIDATION_PASSWORD_NUMBER" });
      }
      if (!hasSpecialChar) {
        return this.createError({ message: "VALIDATION_PASSWORD_SPECIAL" });
      }
      if (!hasLetter) {
        return this.createError({ message: "VALIDATION_PASSWORD_LETTER" });
      }
      // If only one type is missing, return the generic error
      return this.createError({ message: "VALIDATION_PASSWORD_REQUIREMENTS" });
    }),
  repeatPassword: Yup.string()
    .oneOf([Yup.ref("password")], "VALIDATION_PASSWORDS_MUST_MATCH")
    .required("VALIDATION_CONFIRM_NEW_PASSWORD"),
});
export const changePasswordFormSchema = passwordValidationSchema.concat(
  Yup.object().shape({
    currentPassword: Yup.string().required(t("VALIDATION_REQUIRED_PASSWORD")),
  })
);
